import publicKey from "../constants/publicKey";

/**
 * Validates a JWT-based software license for authenticity, integrity, and expiration.
 * This function performs comprehensive license validation including signature verification
 * and expiration checking to ensure the license is valid and hasn't been tampered with.
 * 
 * @async
 * @function validateLicense
 * @param {string} license - The JWT license token to validate
 * @returns {Promise<Object>} Validation result object
 * @returns {boolean} returns.isValid - Whether the license is valid
 * @returns {string|null} returns.error - Error message if validation failed, null if successful
 * @returns {Object|null} returns.decodedData - Decoded JWT payload if valid, null if invalid
 * 
 * @example
 * // Valid license
 * const result = await validateLicense("eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9...");
 * console.log(result.isValid); // true
 * console.log(result.decodedData); // { exp: 1234567890, features: ["premium"], ... }
 * 
 * @example
 * // Invalid license
 * const result = await validateLicense("invalid-token");
 * console.log(result.isValid); // false
 * console.log(result.error); // "Invalid JWT token format"
 */
export const validateLicense = async (license) => {
  try {

    //parsing the license
    const jwtData = parseJWTToken(license);

    if (!jwtData) {
      return {
        isValid: false,
        error: "Invalid JWT token format",
        decodedData: null
      };
    }


    //Signature verification
    const isSignatureValid = await verifyJWTSignature(jwtData, license);

    if (!isSignatureValid) {
      return {
        isValid: false,
        error: "Invalid license signature",
        decodedData: null
      };
    }

    //License expiration check
    const isExpired = isLicenseExpired(jwtData.payload);

    if (isExpired) {
      return {
        isValid: false,
        error: "License has expired",
        decodedData: jwtData.payload
      };
    }

    return {
      isValid: true,
      error: null,
      decodedData: jwtData.payload
    };

  } catch (error) {
    return {
      isValid: false,
      error: `License validation error: ${error.message}`,
      decodedData: null
    };
  }
};



/**
 * Parses a JWT token into its constituent parts (header, payload, signature).
 * This function safely decodes the base64url-encoded header and payload sections
 * of a JWT token and returns the parsed data along with the signature.
 * 
 * @function parseJWTToken
 * @param {string} jwtToken - The JWT token string to parse (format: header.payload.signature)
 * @returns {Object|null} Parsed JWT data or null if parsing fails
 * @returns {Object} returns.header - Decoded JWT header containing algorithm and type info
 * @returns {Object} returns.payload - Decoded JWT payload containing license claims
 * @returns {string} returns.signature - The signature part of the JWT token
 * 
 * @example
 * const jwtData = parseJWTToken("eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9.eyJleHAiOjEyMzQ1Njc4OTB9.signature");
 * console.log(jwtData.header); // { alg: "RS256", typ: "JWT" }
 * console.log(jwtData.payload); // { exp: 1234567890, features: ["premium"] }
 * console.log(jwtData.signature); // "signature"
 */
const parseJWTToken = (jwtToken) => {
  try {
    const parts = jwtToken.split(".");

    if (parts.length !== 3) {
      return null;
    }

    const [headerPart, payloadPart, signaturePart] = parts;

    let header;
    try {
      const headerJson = atob(padBase64Url(headerPart));
      header = JSON.parse(headerJson);
    } catch (error) {
      return null;
    }

    let payload;
    try {
      const payloadJson = atob(padBase64Url(payloadPart));
      payload = JSON.parse(payloadJson);
    } catch (error) {
      return null;
    }

    return {
      header,
      payload,
      signature: signaturePart
    };

  } catch (error) {
    return null;
  }
};

/**
 * Verifies the cryptographic signature of a JWT token to ensure authenticity and integrity.
 * This function validates that the JWT token hasn't been tampered with by checking
 * the digital signature against the public key. It enforces RS256 algorithm for security.
 * 
 * @async
 * @function verifyJWTSignature
 * @param {Object} jwtData - Parsed JWT data containing header, payload, and signature
 * @param {string} originalToken - The original JWT token string for signature verification
 * @returns {Promise<boolean>} True if signature is valid, false otherwise
 * 
 * @example
 * const jwtData = parseJWTToken("eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9...");
 * const isValid = await verifyJWTSignature(jwtData, "eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9...");
 * console.log(isValid); // true or false
 */
const verifyJWTSignature = async (jwtData, originalToken) => {

  try {
    const parts = originalToken.split(".");
    const headerAndPayload = `${parts[0]}.${parts[1]}`;
    const signature = parts[2];

    if (!signature || signature.length === 0) {
      return false;
    }

    // Enforce RS256 algorithm to prevent algorithm confusion attacks
    const algorithm = jwtData.header.alg;
    if (algorithm !== 'RS256') {
      return false;
    }

    // Use Web Crypto API (browser only)
    if (typeof window !== 'undefined' && window.crypto && window.crypto.subtle) {
      return await verifyWithWebCrypto(headerAndPayload, signature);
    }

    return false;

  } catch (error) {
    return false;
  }

};

/**
 * Performs cryptographic signature verification using the Web Crypto API.
 * This function uses the browser's built-in cryptographic functions to verify
 * RSA signatures with SHA-256 hashing, ensuring the JWT token is authentic.
 * 
 * @async
 * @function verifyWithWebCrypto
 * @param {string} headerAndPayload - The header and payload parts of the JWT (header.payload)
 * @param {string} signature - The base64url-encoded signature to verify
 * @returns {Promise<boolean>} True if signature verification succeeds, false otherwise
 * 
 * @example
 * const isValid = await verifyWithWebCrypto("eyJhbGciOiJSUzI1NiJ9.eyJleHAiOjEyMzQ1Njc4OTB9", "signature");
 * console.log(isValid); // true or false
 */
const verifyWithWebCrypto = async (headerAndPayload, signature) => {

  try {

    let publicKeyPem = publicKey.trim();


    const keyData = pemToArrayBuffer(publicKeyPem);

    const publicKeyCrypto = await window.crypto.subtle.importKey(
      'spki',
      keyData,
      {
        name: 'RSASSA-PKCS1-v1_5',
        hash: 'SHA-256'
      },
      false,
      ['verify']
    );


    // Convert signature from base64url to ArrayBuffer with proper padding
    const signatureBuffer = base64UrlToArrayBuffer(signature);

    // Convert headerAndPayload to ArrayBuffer
    const dataBuffer = new TextEncoder().encode(headerAndPayload);

    // Verify the signature
    const isValid = await window.crypto.subtle.verify(
      'RSASSA-PKCS1-v1_5',
      publicKeyCrypto,
      signatureBuffer,
      dataBuffer
    );

    return isValid;

  } catch (error) {
    return false;
  }

};


/**
 * Converts a PEM-formatted public key to an ArrayBuffer for use with Web Crypto API.
 * This function removes whitespace from the base64 data and converts it to binary format.
 * 
 * @function pemToArrayBuffer
 * @param {string} base64Data - PEM-formatted base64 string (with or without whitespace)
 * @returns {ArrayBuffer} Binary representation of the public key
 * 
 * @example
 * const keyBuffer = pemToArrayBuffer("-----BEGIN PUBLIC KEY-----\nMIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEA...\n-----END PUBLIC KEY-----");
 * console.log(keyBuffer instanceof ArrayBuffer); // true
 */
const pemToArrayBuffer = (base64Data) => {

  // Remove any whitespace from the base64 data
  const base64 = base64Data.replace(/\s/g, '');

  return base64ToArrayBuffer(base64);

};

/**
 * Converts a base64 string to an ArrayBuffer.
 * This function decodes base64 data and converts it to binary format for cryptographic operations.
 * 
 * @function base64ToArrayBuffer
 * @param {string} base64 - Base64-encoded string to convert
 * @returns {ArrayBuffer} Binary representation of the base64 data
 * 
 * @example
 * const buffer = base64ToArrayBuffer("SGVsbG8gV29ybGQ=");
 * console.log(buffer instanceof ArrayBuffer); // true
 */
const base64ToArrayBuffer = (base64) => {

  const binaryString = atob(base64);

  const bytes = new Uint8Array(binaryString.length);

  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return bytes.buffer;

};

/**
 * Converts a base64url string to an ArrayBuffer.
 * This function handles base64url format (used in JWT tokens) by adding proper padding
 * and converting URL-safe characters before decoding to binary format.
 * 
 * @function base64UrlToArrayBuffer
 * @param {string} base64Url - Base64url-encoded string to convert
 * @returns {ArrayBuffer} Binary representation of the base64url data
 * 
 * @example
 * const buffer = base64UrlToArrayBuffer("SGVsbG8gV29ybGQ");
 * console.log(buffer instanceof ArrayBuffer); // true
 */
const base64UrlToArrayBuffer = (base64Url) => {

  const base64 = padBase64Url(base64Url);

  return base64ToArrayBuffer(base64);
};

/**
 * Pads a base64url string to ensure proper base64 decoding.
 * This function converts URL-safe base64url characters to standard base64 format
 * and adds necessary padding characters for proper decoding.
 * 
 * @function padBase64Url
 * @param {string} base64Url - Base64url string that may need padding
 * @returns {string} Properly padded base64 string
 * 
 * @example
 * const padded = padBase64Url("SGVsbG8gV29ybGQ");
 * console.log(padded); // "SGVsbG8gV29ybGQ="
 */
const padBase64Url = (base64Url) => {

  // Replace URL-safe characters
  let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

  // Add padding if needed
  while (base64.length % 4) {
    base64 += '=';
  }

  return base64;

};


/**
 * Checks if a license has expired based on the JWT payload.
 * This function supports two expiration date formats: Unix timestamp (exp) and
 * ISO date string (expiresAt). It compares the expiration date with the current date.
 * 
 * @function isLicenseExpired
 * @param {Object} payload - JWT payload containing expiration information
 * @param {number} [payload.exp] - Unix timestamp expiration date (seconds since epoch)
 * @param {string} [payload.expiresAt] - ISO date string expiration date
 * @returns {boolean} True if license is expired, false if still valid or no expiration set
 * 
 * @example
 * // Expired license (Unix timestamp)
 * const expired = isLicenseExpired({ exp: 1234567890 });
 * console.log(expired); // true (if current time > 1234567890)
 * 
 * @example
 * // Valid license (ISO date)
 * const valid = isLicenseExpired({ expiresAt: "2025-12-31T23:59:59Z" });
 * console.log(valid); // false (if current time < expiration)
 */
const isLicenseExpired = (payload) => {
  if (payload.exp) {
    const expirationDate = new Date(payload.exp * 1000);
    const currentDate = new Date();
    return currentDate > expirationDate;
  }

  if (payload.expiresAt) {
    const expirationDate = new Date(payload.expiresAt);
    const currentDate = new Date();
    return currentDate > expirationDate;
  }

  return false;
};

/**
 * Extracts and formats license information from a JWT license token.
 * This function validates the license first, then extracts and formats useful
 * information like issuer, features, expiration date, and customer details.
 * 
 * @async
 * @function getLicenseInfo
 * @param {string} license - The JWT license token to extract information from
 * @returns {Promise<Object>} License information object
 * @returns {boolean} returns.isValid - Whether the license is valid
 * @returns {string|null} returns.error - Error message if license is invalid
 * @returns {Object|null} returns.info - Formatted license information if valid
 * @returns {string} returns.info.issuer - Who issued the license
 * @returns {string} returns.info.issuedTo - Who the license was issued to
 * @returns {string} returns.info.type - Type of license
 * @returns {Date|null} returns.info.expiresAt - When the license expires
 * @returns {Array} returns.info.features - List of enabled features
 * @returns {string} returns.info.customer - Customer information
 * @returns {Date|null} returns.info.issuedAt - When the license was issued
 * @returns {string} returns.info.version - License version
 * @returns {boolean} returns.info.collectionField - Whether collection field is enabled
 * 
 * @example
 * const licenseInfo = await getLicenseInfo("eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9...");
 * if (licenseInfo.isValid) {
 *   console.log(licenseInfo.info.issuer); // "Joyfill"
 *   console.log(licenseInfo.info.features); // ["premium", "analytics"]
 *   console.log(licenseInfo.info.expiresAt); // Date object
 * }
 */
export const getLicenseInfo = async (license) => {
  const validation = await validateLicense(license);

  if (!validation.isValid) {
    return {
      isValid: false,
      error: validation.error,
      info: null
    };
  }

  const payload = validation.decodedData;

  return {
    isValid: true,
    error: null,
    info: {
      issuer: payload.iss || payload.issuer || "Unknown",
      issuedTo: payload.sub || payload.issuedTo || "Unknown",
      type: payload.type || "Unknown",
      expiresAt: payload.exp ? new Date(payload.exp * 1000) : (payload.expiresAt ? new Date(payload.expiresAt) : null),
      features: payload.features || [],
      customer: payload.customer || "Unknown",
      issuedAt: payload.iat ? new Date(payload.iat * 1000) : (payload.issuedAt ? new Date(payload.issuedAt) : null),
      version: payload.version || "1.0.0",
      collectionField: payload.collectionField || false
    }
  };
};

export default {
  validateLicense,
  getLicenseInfo
};